import clsx from 'clsx';
import React from 'react';
import Hr from '../../atoms/Hr/Hr';
import Icon from '../../atoms/Icon/Icon';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import type { IconColor, IconName } from '../../icons/types';
import type { ListSimpleDropdownIconSize, ListSimpleDropdownItem, ListSimpleDropdownProps } from './types';
import useListSimpleDropdown from './useListSimpleDropdown';

type ItemInnerProps = {
  getColor: (source: ListSimpleDropdownItem) => IconColor;
  getRightIconColor: (source: ListSimpleDropdownItem) => IconColor;
  isTitle: boolean;
  sizeIcon: ListSimpleDropdownIconSize;
  source: ListSimpleDropdownItem;
};

/**
 * Contenido interno de una fila (icono izquierdo + textos + icono derecho).
 * Presentacional puro; compartido por las filas de acción y de título. La
 * estructura DOM replica exactamente la del v1 (11 SCSS externos dependen de
 * este anidamiento). El tooltip envuelve SOLO el bloque de textos.
 */
function ListSimpleDropdownItemInner({ getColor, getRightIconColor, isTitle, sizeIcon, source }: Readonly<ItemInnerProps>): JSX.Element {
  const texts = (
    <div className="list-simple-dropdown__texts">
      <span className={clsx('list-simple-dropdown__name', { 'disabled': source.disabled, 'list-simple-dropdown__title': isTitle })}>{source.name} </span>
      <span className="list-simple-dropdown__description">{source.description} </span>
    </div>
  );

  return (
    <>
      {source.icon && (
        <div className="list-simple-dropdown__icon">
          <Icon color={getColor(source)} name={source.icon as IconName} size={sizeIcon} />
        </div>
      )}

      {source.tooltip ? (
        <Tooltip hasArrow placement="right" title={source.tooltip}>
          {texts}
        </Tooltip>
      ) : (
        texts
      )}

      {source.rightIcon && (
        <div className="list-simple-dropdown__icon list-simple-dropdown__icon--right">
          <Icon color={getRightIconColor(source)} name={source.rightIcon as IconName} size={sizeIcon} />
        </div>
      )}
    </>
  );
}

/**
 * ListSimpleDropdown — v2 molecule.
 *
 * Lista de acciones simple (icono + texto + descripción) para el interior de un
 * dropdown. Migración TypeScript del v1 con accesibilidad de menú WAI-ARIA:
 * `role="menu"` en la raíz, `role="menuitem"` con *roving tabindex* en las filas
 * de acción, `role="separator"` en los separadores y `role="none"` en
 * los títulos. La API de datos se conserva exacta respecto al v1.
 */
function ListSimpleDropdown({
  ariaLabel,
  colorIcon = 'default',
  colorIconActive,
  items = [],
  onClick,
  onCloseSubmenu,
  preventEvent = false,
  shouldAutoFocusFirstItem = false,
  sizeIcon = 'md',
}: Readonly<ListSimpleDropdownProps>): JSX.Element {
  const { getColor, getDescriptionId, getItemProps, getMenuProps, getRightIconColor, groups, isColumns, resolvedAriaLabel } = useListSimpleDropdown({
    ariaLabel,
    colorIcon,
    colorIconActive,
    items,
    onClick,
    onCloseSubmenu,
    preventEvent,
    shouldAutoFocusFirstItem,
  });

  function renderGroup(group: typeof groups[number], labelId?: string): JSX.Element {
    return (
      <ul aria-labelledby={labelId} role={labelId ? 'group' : 'none'} /* NOSONAR S6819: APG pide role="group" en un menú; <fieldset> no cabe en role="menu" */>
        {group.map((entry) => {
          if (entry.kind === 'group') {
            return (
              <li key={entry.key} role="none">
                {renderGroup(entry.entries, entry.labelId)}
              </li>
            );
          }

          if (entry.kind === 'separator') {
            return (
              <li className="list-simple-dropdown__separator" key={entry.key} role="separator">
                <Hr size="small" />
              </li>
            );
          }

          if (entry.kind === 'title') {
            return (
              <li
                className={clsx('list-simple-dropdown__li', 'list-simple-dropdown__li--title', entry.source.className)}
                id={entry.labelId}
                key={entry.key}
                role="none"
              >
                <ListSimpleDropdownItemInner getColor={getColor} getRightIconColor={getRightIconColor} isTitle sizeIcon={sizeIcon} source={entry.source} />
              </li>
            );
          }

          // El tooltip solo se abre con hover (MUI lo engancha al bloque de textos, que no es
          // focalizable), así que su texto se expone además por `aria-describedby` para que el
          // motivo llegue por teclado y lector de pantalla. `aria-hidden` evita que el span
          // entre también en el nombre accesible de la fila.
          const descriptionId = entry.source.tooltip ? getDescriptionId(entry.key) : undefined;

          return (
            <li
              aria-current={entry.isActive ? 'true' : undefined}
              aria-describedby={descriptionId}
              className={clsx(
                'list-simple-dropdown__li',
                { 'list-simple-dropdown__li--active': entry.isActive, 'list-simple-dropdown__li--disabled': entry.isDisabled },
                entry.source.className,
              )}
              key={entry.key}
              {...getItemProps(entry.globalIndex)}
            >
              <ListSimpleDropdownItemInner
                getColor={getColor}
                getRightIconColor={getRightIconColor}
                isTitle={false}
                sizeIcon={sizeIcon}
                source={entry.source}
              />
              {descriptionId && (
                <span aria-hidden="true" className="sr-only" id={descriptionId}>
                  {entry.source.tooltip}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div aria-label={resolvedAriaLabel} className="list-simple-dropdown" {...getMenuProps()}>
      {isColumns
        ? groups.map((group, index) => (
            <div key={group[0]?.key ?? `column-${index}`} role="none">
              {renderGroup(group)}
            </div>
          ))
        : renderGroup(groups[0] ?? [])}
    </div>
  );
}

export default ListSimpleDropdown;
